<template>
  <v-container class="pics-page" fluid>
    <v-card class="mx-auto" max-width="1100">
      <v-card-title class="d-flex align-center">
        <v-btn text v-on:click="$router.go(-1)">
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn-toggle
          v-model="language"
          aria-label="Statement language"
          mandatory
          color="primary"
        >
          <v-btn value="zh-HK">繁體中文</v-btn>
          <v-btn value="en">English</v-btn>
        </v-btn-toggle>
      </v-card-title>

      <v-card-text>
        <article :lang="language" aria-live="polite" class="statement">
          <h1>{{ statement.title }}</h1>

          <p v-for="(paragraph, index) in statement.introduction" :key="index">
            {{ paragraph }}
          </p>

          <section
            v-for="(section, index) in statement.sections"
            :key="index"
            class="statement-section"
          >
            <h2>{{ section.heading }}</h2>

            <p v-for="(paragraph, paragraphIndex) in section.paragraphs" :key="paragraphIndex">
              {{ paragraph }}
            </p>

            <ul v-if="section.bullets.length">
              <li v-for="(bullet, bulletIndex) in section.bullets" :key="bulletIndex">
                <strong v-if="bullet.label">{{ bullet.label }} </strong>{{ bullet.text }}
              </li>
            </ul>

            <p v-if="section.emailLabel" class="contact-email">
              <strong>{{ section.emailLabel }} </strong>{{ section.email }}
            </p>
          </section>
        </article>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
const statements = {
  en: {
    title: 'PERSONAL INFORMATION COLLECTION STATEMENT (PICS)',
    introduction: [
      'This Personal Information Collection Statement ("PICS") is provided by the bfGPS team ("we", "us", or "our") in accordance with the Personal Data (Privacy) Ordinance (Cap. 486) of Hong Kong. This statement is intended to clarify the arrangements for how we collect, use, and process your personal data when you use bfGPS.',
      'Downloading the bfGPS and using its database searching functions does not require registration, and no personal data is collected for general browsing. This PICS applies only if you choose to register an account. You are only required to provide a valid phone number to register an account if you wish to become a contributor or reviewer.'
    ],
    sections: [
      {
        heading: '1. Purpose of Collection',
        paragraphs: ['We collect and process your personal data strictly for the following limited purposes:'],
        bullets: [
          {
            label: 'Telephone Numbers:',
            text: 'The telephone number you provide during registration is collected strictly for account management and research purposes only. It will not be used for marketing, sales, or commercial communications.'
          },
          {
            label: 'Uploaded Photos:',
            text: 'Photos uploaded by registered users to help update the database and to populate the interactive features of the platform. These photos will be displayed to other users who search or browse the app. To ensure safety and accuracy, the bfGPS App team manually reviews and approves all submitted facility details and photos before they are visible to the public within the app. They will be used exclusively within the bfGPS App ecosystem to facilitate community sharing and will not be repurposed or used for external projects.'
          }
        ]
      },
      {
        heading: '2. Voluntary Nature of Registration and Data Provision',
        paragraphs: [],
        bullets: [
          {
            label: 'Searching is Anonymous:',
            text: 'You can freely download the app and use the searching functions anonymously without providing any personal data.'
          },
          {
            label: 'Optional Registration:',
            text: 'Account registration is entirely voluntary. It is only required if you wish to help edit/update our database or submit reviews of breastfeeding facilities.'
          },
          {
            label: 'Obligatory Fields for Contributors/Reviewers:',
            text: 'If you choose to become a contributor or reviewer, providing a valid telephone number is mandatory to complete the registration process for account verification and accountability. Failure to provide it means you will not be able to submit reviews or edit the database, though you can still fully use the searching functions.'
          }
        ]
      },
      {
        heading: '3. Disclosure and Sharing of Data',
        paragraphs: [
          'Your personal data will be kept confidential. However, by registering and contributing content, you acknowledge and agree that:'
        ],
        bullets: [
          {
            label: '',
            text: 'Your photos and written reviews will be visible to all users of the bfGPS (including unregistered users) as part of the public database search results and community reviews.'
          },
          {
            label: '',
            text: 'Your phone number will remain strictly hidden from the public and will only be accessible to authorized research personnel and backend technical administrators bound by strict confidentiality.'
          },
          {
            label: '',
            text: 'We will never disclose your personal data to external commercial third parties unless required by law.'
          }
        ]
      },
      {
        heading: '4. Data Security and Retention',
        paragraphs: [
          'We implement appropriate measures to safeguard your data. Your phone number, submitted reviews, and uploaded photos will only be retained for as long as necessary to fulfill the research and operational purposes of the platform, or until you request account deletion.'
        ],
        bullets: []
      },
      {
        heading: '5. Access and Correction of Personal Data',
        paragraphs: [
          'You have the right to request access to, and correction or deletion of, the personal data we hold about you. If you wish to modify or remove your telephone number, reviews, or uploaded photos from our database, please contact us at:'
        ],
        bullets: [],
        emailLabel: 'Email:',
        email: 'bfci@hku.hk'
      }
    ]
  },
  'zh-HK': {
    title: '收集個人資料聲明 (PICS)',
    introduction: [
      '本《收集個人資料聲明》（「本聲明」）由 bfGPS 團隊（下稱「我們」）根據香港法例第 486 章《個人資料（私隱）條例》提供。本聲明旨在闡明當您使用 bfGPS 時，我們收集、使用及處理您個人資料的安排。',
      '下載 bfGPS 及使用其資料庫搜尋功能毋須進行註冊，我們亦不會為一般瀏覽活動收集任何個人資料。本聲明僅適用於您選擇註冊帳戶的情況。只有在您希望提交評論或編輯資料庫內容時，才需要提供有效電話號碼以作註冊帳戶之用。'
    ],
    sections: [
      {
        heading: '1. 收集資料的目的',
        paragraphs: ['我們嚴格基於以下有限目的收集及處理您的個人資料：'],
        bullets: [
          {
            label: '電話號碼：',
            text: '您在註冊時提供的電話號碼純粹用於帳戶管理和研究用途。該資料絕不會用於市場營銷、銷售或任何商業通訊。'
          },
          {
            label: '上載的照片：',
            text: '由註冊用家上載以協助更新資料庫的照片，將用於豐富本平台的互動功能。這些照片將會向其他用戶顯示（當他們在程式內進行搜尋或瀏覽時）。為確保安全與準確，bfGPS團隊將對所有提交的設施詳情及照片進行人工審核與批准；只有在通過審核後，這些內容才會向公眾展示。該等照片將僅限於 bfGPS App 生態系統內使用以促進社區分享，絕不會被轉作其他用途或用於任何外部項目。'
          }
        ]
      },
      {
        heading: '2. 註冊及提供資料的自願性質',
        paragraphs: [],
        bullets: [
          {
            label: '匿名搜尋：',
            text: '您可以自由下載本應用程式並匿名使用搜尋功能，毋須提供任何個人資料。'
          },
          {
            label: '自願註冊：',
            text: '帳戶註冊完全屬於自願性質。帳戶註冊僅在您希望協助編輯／更新我們的資料庫，或提交母乳餵哺設施的評論時才需要。'
          },
          {
            label: '貢獻者／評論者的必填欄位：',
            text: '若您選擇成為貢獻者或評論者，提供有效的電話號碼為完成註冊程序的必要條件，以作帳戶驗證及查詢之用。如未能提供電話號碼，您將無法提交評論或編輯資料庫，但您仍能完全使用搜尋功能。'
          }
        ]
      },
      {
        heading: '3. 資料的披露與轉移',
        paragraphs: ['您的個人資料將予以保密。然而，當您註冊並貢獻內容，即代表您知悉並同意：'],
        bullets: [
          {
            label: '',
            text: '您的照片及評論將向所有 bfGPS 用戶顯示（包括未註冊的用戶），作為公開資料庫搜尋結果及社區評論的一部分。'
          },
          {
            label: '',
            text: '您的電話號碼將嚴格對外保密，不會公開，且僅限於受嚴格保密協議約束的獲授權研究人員及後台技術管理人員查閱。'
          },
          {
            label: '',
            text: '除非法律要求，否則我們絕不會向任何外部商業第三方披露您的個人資料。'
          }
        ]
      },
      {
        heading: '4. 資料安全與保存',
        paragraphs: [
          '我們採取適當措施以保障您的資料安全。您的電話號碼、提交的評論及上載的照片只會因應滿足本平台的研究及營運目的而被保存，或直至您要求刪除帳戶為止。'
        ],
        bullets: []
      },
      {
        heading: '5. 查閱及更正個人資料',
        paragraphs: [
          '根據《個人資料（私隱）條例》，您有權要求查閱、更正或刪除我們所持有的您的個人資料。若您希望修改或從我們的資料庫中刪除您的電話號碼、評論或上載的照片，請聯絡我們：'
        ],
        bullets: [],
        emailLabel: '電子郵件：',
        email: 'bfci@hku.hk'
      }
    ]
  }
};

export default {
  name: 'Pics',
  data: () => ({
    language: 'zh-HK'
  }),
  computed: {
    statement() {
      return statements[this.language];
    }
  }
};
</script>

<style scoped>
.pics-page {
  padding: 24px;
}

.statement {
  color: #212121;
  font-size: 16px;
  line-height: 1.7;
  margin: 0 auto;
  max-width: 900px;
  overflow-wrap: anywhere;
}

.statement h1 {
  font-size: 1.6rem;
  line-height: 1.3;
  margin-bottom: 24px;
}

.statement h2 {
  font-size: 1.2rem;
  line-height: 1.4;
  margin: 28px 0 12px;
}

.statement p {
  margin-bottom: 16px;
}

.statement ul {
  margin: 0 0 16px;
  padding-left: 28px;
}

.statement li + li {
  margin-top: 8px;
}

.contact-email {
  margin-top: 16px;
}

@media (max-width: 600px) {
  .pics-page {
    padding: 12px;
  }

  .statement {
    font-size: 15px;
  }

  .statement h1 {
    font-size: 1.35rem;
  }
}
</style>
